import { memo, FC, useState, useEffect, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil, values } from 'lodash';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useGoToRoute from 'hooks/GoToRoute';
import useQueryParams from 'hooks/UseQueryParams';
import useToaster from 'hooks/UseToaster';
// Import Constants
import l from 'constants/localizationLabels';
import { RoutesList } from 'routes';
import { CampaignStatusLabels, ModalsActions, SegmentTypesLabels, TemplateTypesLabels } from 'constants/common';
// Import Services
import { UtilsHttpService } from 'services/http';
import { recursiveSumObject } from 'services/Helpers';
// Import UI components
import { Icon, SkeletonLoader } from '@geneui/components';
import { CampaignActionsButtons, CampaignActionsModals } from 'components/CampaignList';
import { modalsStateSetDictKeys } from 'pages/CampaignListPage/config';
import SegmentActionsModals from 'components/SegmentList/SegmentActionsModals';
import TemplateActionsModals from 'components/TemplateList/TemplateActionsModals';
// Import Enums
import { CampaignExecutionStatusEnum, CommunicationStatusEnum } from 'enums';
// Import Styles
import 'assets/scss/pageHeadline.scss';
import styles from './campaignReportPageHeadline.module.scss';

const isInReportPage = true;

const {
    CAMPAIGN_LIST,
    CAMPAIGN_EDIT,
    CAMPAIGN_CLONE,
    TEMPLATE_EDIT_EMAIL,
    TEMPLATE_EDIT_SMS,
    TEMPLATE_EDIT_INTERNAL_MESSAGE,
    TEMPLATE_EDIT_SOCIAL_TELEGRAM,
    TEMPLATE_EDIT_PUSH_NOTIFICATION,
    TEMPLATE_EDIT_POP_UP_NOTIFICATION,
    TEMPLATE_EDIT_DYNAMIC_POP_UP,
    TEMPLATE_EDIT_CMS_POP_UP_NOTIFICATION,
    TEMPLATE_EDIT_CMS_BANNER,
    TEMPLATE_EDIT_SOCIAL_VIBER,
    TEMPLATE_EDIT_SOCIAL_WHATS_APP,
    TEMPLATE_EDIT_SOCIAL_WHATS_APP_PROVIDER,
    SEGMENT_DYNAMIC_EDIT,
    SEGMENT_COMPOSITE_EDIT,
} = RoutesList;

interface Props {
    isLoading?: boolean;
    isLoadingExecutionResults?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    campaignData: Record<string, any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    segmentData: Record<string, any>;
    externalRightPart: React.ReactNode;
    reloadCampaignData: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    lastExecutionData: Record<string, any> | null;
}
interface CommunicationStats {
    [channel: string]: {
        [status: string]: number;
    };
}

const CampaignReportPageHeadline: FC<Props> = (props) => {
    const {
        isLoading,
        campaignData,
        segmentData,
        externalRightPart,
        reloadCampaignData,
        isLoadingExecutionResults,
        lastExecutionData,
    } = props;
    const { t } = useTranslation();
    const { showToaster } = useToaster();
    const { doPostRequest } = useRequest();
    const { goToPreviousRoute, goToRoute, getRoutePath } = useGoToRoute();
    const { search } = useLocation();
    const goToRouteQueryParams = useQueryParams();

    const [templateProviders, setTemplateProviders] = useState({});

    const [openedModalData, setOpenedModalData] = useState({});

    const [isInfoModalOpenedState, setIsInfoModalOpenedState] = useState(false);
    const [isDeleteModalOpenedState, setIsDeleteModalOpenedState] = useState(false);
    const [isArchiveModalOpenedState, setIsArchiveModalOpenedState] = useState(false);
    const [isUnArchiveModalOpenedState, setIsUnArchiveModalOpenedState] = useState(false);
    const [isStopSendModalOpenedState, setIsStopSendModalOpenedState] = useState(false);
    const [isAddGoalModalOpenedState, setIsAddGoalModalOpenedState] = useState(false);
    const [isDeleteGoalModalOpenedState, setIsDeleteGoalModalOpenedState] = useState(false);
    const [isStopABTestModalOpenedState, setIsStopABTestModalOpenedState] = useState(false);

    // Template Tab Action Modals States
    const [isTemplateDeleteModalOpenedState, setIsTemplateDeleteModalOpenedState] = useState(false);
    const [isTestTemplateModalOpenedState, setIsTestTemplateModalOpenedState] = useState(false);
    const [isTemplateArchiveModalOpenedState, setIsTemplateArchiveModalOpenedState] = useState(false);
    const [isTemplateUnArchiveModalOpenedState, setIsTemplateUnArchiveModalOpenedState] = useState(false);
    const [isTemplateUsedInConfirmationModalOpenedState, setIsTemplateUsedInConfirmationModalOpenedState] =
        useState(false);

    // Segment Tab Action Modals States
    const [isSegmentDeleteModalOpenedState, setIsSegmentDeleteModalOpenedState] = useState(false);
    const [isConvertToReportModalOpenedState, setIsConvertToReportModalOpenedState] = useState(false);
    const [isSegmentArchiveModalOpenedState, setIsSegmentArchiveModalOpenedState] = useState(false);
    const [isSegmentUnArchiveModalOpenedState, setIsSegmentUnArchiveModalOpenedState] = useState(false);
    const [isSegmentUsedInConfirmationModalOpenedState, setIsSegmentUsedInConfirmationModalOpenedState] =
        useState(false);

    const { getMessageProvidersRequest } = useCancelableRequests({
        getMessageProvidersRequest: UtilsHttpService.getPartnerMessageProviders,
    });

    useEffect(() => {
        if (isInfoModalOpenedState) {
            const getTemplateProviders = (channel) => {
                doPostRequest(getMessageProvidersRequest.request, {
                    requestBody: { DeliveryMethod: channel },
                    successCallback: (Data) => {
                        setTemplateProviders((prev) => {
                            prev[channel] = (isNil(Data) ? [] : Data).reduce((acc, item) => {
                                acc[item.Id] = { label: item.Name, value: item.Id };
                                return acc;
                            }, {});
                            return { ...prev };
                        });
                    },
                });
            };

            getTemplateProviders(TemplateTypesLabels.SMS);
            getTemplateProviders(TemplateTypesLabels.Email);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInfoModalOpenedState]);

    // Actions part
    const deleteCampaignAction = useCallback((campaign) => {
        setOpenedModalData(campaign);
        setIsDeleteModalOpenedState(true);
    }, []);

    const archiveCampaignAction = useCallback((campaign) => {
        setOpenedModalData(campaign);
        setIsArchiveModalOpenedState(true);
    }, []);

    const unArchiveCampaignAction = useCallback((campaign) => {
        setOpenedModalData(campaign);
        setIsUnArchiveModalOpenedState(true);
    }, []);

    const stopSendCampaignAction = useCallback((campaign) => {
        setOpenedModalData(campaign);
        setIsStopSendModalOpenedState(true);
    }, []);

    const stopABTestCampaignAction = useCallback((campaign) => {
        setOpenedModalData(campaign);
        setIsStopABTestModalOpenedState(true);
    }, []);

    const infoCampaignAction = useCallback((campaign) => {
        setOpenedModalData(campaign);
        setIsInfoModalOpenedState(true);
    }, []);

    const getCloneCampaignPath = useCallback(
        (campaign) => {
            const { CampaignId } = campaign;
            const params = { campaignId: CampaignId };
            return getRoutePath(CAMPAIGN_CLONE, params);
        },
        [getRoutePath],
    );

    const getEditCampaignPath = useCallback(
        (campaign) => {
            const { CampaignId } = campaign;
            const params = { campaignId: CampaignId };
            return getRoutePath(CAMPAIGN_EDIT, params);
        },
        [getRoutePath],
    );

    const modalsStateSetDict = useMemo(
        () => ({
            [modalsStateSetDictKeys.deleteCampaign]: {
                key: modalsStateSetDictKeys.deleteCampaign,
                fn: setIsDeleteModalOpenedState,
            },
            [modalsStateSetDictKeys.archiveCampaign]: {
                key: modalsStateSetDictKeys.archiveCampaign,
                fn: setIsArchiveModalOpenedState,
            },
            [modalsStateSetDictKeys.unarchiveCampaign]: {
                key: modalsStateSetDictKeys.unarchiveCampaign,
                fn: setIsUnArchiveModalOpenedState,
            },
            [modalsStateSetDictKeys.stopSendCampaign]: {
                key: modalsStateSetDictKeys.stopSendCampaign,
                fn: setIsStopSendModalOpenedState,
            },
            [modalsStateSetDictKeys.infoCampaign]: {
                key: modalsStateSetDictKeys.infoCampaign,
                fn: setIsInfoModalOpenedState,
            },
            [modalsStateSetDictKeys.setGoalCampaign]: {
                key: modalsStateSetDictKeys.setGoalCampaign,
                fn: setIsAddGoalModalOpenedState,
            },
            [modalsStateSetDictKeys.deleteGoalCampaign]: {
                key: modalsStateSetDictKeys.deleteGoalCampaign,
                fn: setIsDeleteGoalModalOpenedState,
            },
            [modalsStateSetDictKeys.stopABTest]: {
                key: modalsStateSetDictKeys.stopABTest,
                fn: setIsStopABTestModalOpenedState,
            },

            [modalsStateSetDictKeys.deleteTemplate]: {
                key: 'deleteTemplate',
                fn: setIsTemplateDeleteModalOpenedState,
            },
            [modalsStateSetDictKeys.testTemplate]: {
                key: 'testTemplate',
                fn: setIsTestTemplateModalOpenedState,
            },
            [modalsStateSetDictKeys.archiveTemplate]: {
                key: 'archiveTemplate',
                fn: setIsTemplateArchiveModalOpenedState,
            },
            [modalsStateSetDictKeys.unarchiveTemplate]: {
                key: 'unarchiveTemplate',
                fn: setIsTemplateUnArchiveModalOpenedState,
            },
            [modalsStateSetDictKeys.editTemplate]: {
                key: 'editTemplate',
                fn: setIsTemplateUsedInConfirmationModalOpenedState,
            },

            [modalsStateSetDictKeys.deleteSegment]: {
                key: 'deleteSegment',
                fn: setIsSegmentDeleteModalOpenedState,
            },
            [modalsStateSetDictKeys.convertToReport]: {
                key: 'convertToReport',
                fn: setIsConvertToReportModalOpenedState,
            },
            [modalsStateSetDictKeys.archiveSegment]: {
                key: 'archiveSegment',
                fn: setIsSegmentArchiveModalOpenedState,
            },
            [modalsStateSetDictKeys.unarchiveSegment]: {
                key: 'unarchiveSegment',
                fn: setIsSegmentUnArchiveModalOpenedState,
            },

            [modalsStateSetDictKeys.editSegment]: {
                key: modalsStateSetDictKeys.editSegment,
                fn: setIsSegmentUsedInConfirmationModalOpenedState,
            },
        }),
        [],
    );

    const handleInfoModalActionClick = useCallback(
        (modalKey, data) => {
            setIsInfoModalOpenedState(false);
            if (!isNil(modalKey) && !isNil(data)) {
                setOpenedModalData(data);
                modalsStateSetDict[modalKey].fn(true);
            }
        },
        [modalsStateSetDict],
    );

    const onCloseModalHandler = (modalStateKey, action, alertType, alertMessage) => {
        if (
            action === ModalsActions.DELETE ||
            action === ModalsActions.ARCHIVE ||
            action === ModalsActions.UNARCHIVE ||
            action === ModalsActions.STOP ||
            action === ModalsActions.SET_GOAL ||
            action === ModalsActions.DELETE_GOAL ||
            action === ModalsActions.CONVERT_TO_REPORT ||
            action === ModalsActions.TEST_TEMPLATE ||
            action === ModalsActions.STOP_AB_TEST
        ) {
            showToaster(alertType, alertMessage);

            if (action === ModalsActions.DELETE) {
                goToRoute(CAMPAIGN_LIST);
            } else {
                reloadCampaignData();
            }
        }

        modalsStateSetDict[modalStateKey].fn(false);
    };

    // Template Actions Part
    const goToEditTemplate = useCallback(
        (template) => {
            const { DeliveryMethod, TemplateId } = template;
            const params = { templateId: TemplateId };
            let path;

            switch (DeliveryMethod) {
                case TemplateTypesLabels.Email:
                    path = TEMPLATE_EDIT_EMAIL;
                    break;
                case TemplateTypesLabels.SMS:
                    path = TEMPLATE_EDIT_SMS;
                    break;
                case TemplateTypesLabels.Internal:
                    path = TEMPLATE_EDIT_INTERNAL_MESSAGE;
                    break;
                case TemplateTypesLabels.Telegram:
                    path = TEMPLATE_EDIT_SOCIAL_TELEGRAM;
                    break;
                case TemplateTypesLabels.PushNotification:
                    path = TEMPLATE_EDIT_PUSH_NOTIFICATION;
                    break;
                case TemplateTypesLabels.PopUp:
                    path = TEMPLATE_EDIT_POP_UP_NOTIFICATION;
                    break;
                case TemplateTypesLabels.DynamicPopUp:
                    path = TEMPLATE_EDIT_DYNAMIC_POP_UP;
                    break;
                case TemplateTypesLabels.CMSPopUp:
                    path = TEMPLATE_EDIT_CMS_POP_UP_NOTIFICATION;
                    break;
                case TemplateTypesLabels.CMSBanner:
                    path = TEMPLATE_EDIT_CMS_BANNER;
                    break;
                case TemplateTypesLabels.Viber:
                    path = TEMPLATE_EDIT_SOCIAL_VIBER;
                    break;
                case TemplateTypesLabels.WhatsApp:
                    path = TEMPLATE_EDIT_SOCIAL_WHATS_APP;
                    break;
                case TemplateTypesLabels.WhatsAppProvider:
                    path = TEMPLATE_EDIT_SOCIAL_WHATS_APP_PROVIDER;
                    break;
                default:
            }

            goToRoute(path, params, goToRouteQueryParams.encode({ backParams: search }));
        },
        [goToRoute, goToRouteQueryParams, search],
    );

    // Segment Actions Part
    const goToEditSegment = useCallback(() => {
        const { Type, SegmentId } = segmentData;
        const params = { segmentId: SegmentId };
        const path = SegmentTypesLabels.Dynamic === Type ? SEGMENT_DYNAMIC_EDIT : SEGMENT_COMPOSITE_EDIT;

        goToRoute(path, params, goToRouteQueryParams.encode({ backParams: search }));
    }, [goToRoute, goToRouteQueryParams, search, segmentData]);

    const sumNonPendingExecutions = (stats: CommunicationStats, pendingStatus = CommunicationStatusEnum.Pending) => {
        let total = 0;
        for (const statusCounts of Object.values(stats)) {
            for (const [status, count] of Object.entries(statusCounts)) {
                if (Number(status) !== pendingStatus) {
                    total += recursiveSumObject(count);
                }
            }
        }

        return total;
    };

    const lastExecutionRatesPerSegmentSize = useMemo(() => {
        if (lastExecutionData?.StatusEnum !== CampaignExecutionStatusEnum.Sending) {
            return '';
        }
        const nonPendingExecutions = sumNonPendingExecutions(
            lastExecutionData?.Stats || {},
            CommunicationStatusEnum.Pending,
        );

        return `${nonPendingExecutions} / ${lastExecutionData.SegmentSize ?? 0}`;
    }, [lastExecutionData]);

    return (
        <div className={styles.campaignPageHeadline}>
            <div className={styles.backButtonContainer}>
                <Icon type="bc-icon-arrow-back" onClick={goToPreviousRoute} />
            </div>

            <div className={styles.content}>
                <div className={styles.leftPart}>
                    <div className={styles.titlePanel}>
                        <SkeletonLoader isBusy={isLoading} height="20px" width="150px">
                            <div className={styles.titleText}>{campaignData?.Name?.Name}</div>
                        </SkeletonLoader>

                        <SkeletonLoader isBusy={isLoading} height="20px" width="150px">
                            <div className={styles.contextText}>
                                {isEmpty(campaignData?.Description) ? t(l.NA) : campaignData?.Description}
                            </div>
                        </SkeletonLoader>
                    </div>

                    <div className={styles.infoPanel}>
                        <SkeletonLoader isBusy={isLoading} height="45px" width="100px">
                            <div className={styles.titleText}>{t(l.Id)}</div>
                            <div className={styles.contextText}>{campaignData?.CampaignId}</div>
                        </SkeletonLoader>
                    </div>

                    <div className={styles.infoPanel}>
                        <SkeletonLoader isBusy={isLoading} height="45px" width="100px">
                            <div className={styles.titleText}>{t(l.Status)}</div>
                            <div
                                className={classNames(
                                    styles.contextText,
                                    `crm-status-badge-cnt crm-cj-status-label-${campaignData?.Status}`,
                                )}
                            >
                                {t(CampaignStatusLabels[campaignData?.Status])}
                            </div>
                        </SkeletonLoader>
                    </div>
                    {lastExecutionData?.Status && (
                        <div className={styles.infoPanel}>
                            <SkeletonLoader isBusy={isLoadingExecutionResults} height="45px" width="100px">
                                <div className={styles.titleText}>{t(l.LastExecutionStatus)}</div>
                                <div className={styles.contextText}>
                                    {t(lastExecutionData?.Status)} {lastExecutionRatesPerSegmentSize}
                                </div>
                            </SkeletonLoader>
                        </div>
                    )}
                </div>
                <div className={styles.rightPart}>
                    <SkeletonLoader isBusy={isLoading} height="40px" width="200px">
                        <div className={styles.actionsContainer}>
                            <CampaignActionsButtons
                                isInReportPage={isInReportPage}
                                isInInfoModal={false}
                                data={campaignData || {}}
                                NextRunDate={campaignData.NextRunDate}
                                unArchiveCampaignAction={unArchiveCampaignAction}
                                archiveCampaignAction={archiveCampaignAction}
                                deleteCampaignAction={deleteCampaignAction}
                                getCloneCampaignPath={getCloneCampaignPath}
                                getEditCampaignPath={getEditCampaignPath}
                                stopSendCampaignAction={stopSendCampaignAction}
                                infoCampaignAction={infoCampaignAction}
                                stopABTestCampaignAction={stopABTestCampaignAction}
                            />
                        </div>
                    </SkeletonLoader>

                    {externalRightPart}
                </div>

                <CampaignActionsModals
                    isInReportPage={isInReportPage}
                    isDeleteModalOpenedState={isDeleteModalOpenedState}
                    isArchiveModalOpenedState={isArchiveModalOpenedState}
                    isUnArchiveModalOpenedState={isUnArchiveModalOpenedState}
                    isStopSendModalOpenedState={isStopSendModalOpenedState}
                    isInfoModalOpenedState={isInfoModalOpenedState}
                    isAddGoalModalOpenedState={isAddGoalModalOpenedState}
                    isDeleteGoalModalOpenedState={isDeleteGoalModalOpenedState}
                    isStopABTestModalOpenedState={isStopABTestModalOpenedState}
                    onCloseModalHandler={onCloseModalHandler}
                    openedModalData={openedModalData}
                    handleInfoModalActionClick={handleInfoModalActionClick}
                    templateProviders={templateProviders}
                />
                <SegmentActionsModals
                    isInfoModalOpenedState={false}
                    isDeleteModalOpenedState={isSegmentDeleteModalOpenedState}
                    isConvertToReportModalOpenedState={isConvertToReportModalOpenedState}
                    isArchiveModalOpenedState={isSegmentArchiveModalOpenedState}
                    isUnArchiveModalOpenedState={isSegmentUnArchiveModalOpenedState}
                    isUsedInConfirmationModalOpenedState={isSegmentUsedInConfirmationModalOpenedState}
                    onCloseModalHandler={onCloseModalHandler}
                    modalsStateSetDict={modalsStateSetDict}
                    openedModalData={openedModalData}
                    goToEditSegment={goToEditSegment}
                />
                <TemplateActionsModals
                    isDeleteModalOpenedState={isTemplateDeleteModalOpenedState}
                    isArchiveModalOpenedState={isTemplateArchiveModalOpenedState}
                    isUnArchiveModalOpenedState={isTemplateUnArchiveModalOpenedState}
                    isUsedInConfirmationModalOpenedState={isTemplateUsedInConfirmationModalOpenedState}
                    isTestTemplateModalOpenedState={isTestTemplateModalOpenedState}
                    onCloseModalHandler={onCloseModalHandler}
                    modalsStateSetDict={modalsStateSetDict}
                    openedModalData={openedModalData}
                    goToEditTemplate={goToEditTemplate}
                    providers={values(templateProviders[campaignData?.DeliveryMethod])}
                />
            </div>
        </div>
    );
};

export default memo(CampaignReportPageHeadline);
